import { createClient, SupabaseClient, User } from '@supabase/supabase-js';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getApiUrl } from '../utils/apiUrl';

interface AuthContextType {
  user: User | null;
  supabase: SupabaseClient;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithUsernameOrEmail: (usernameOrEmail: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username?: string) => Promise<void>;
  signUpWithUsername: (username: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  loading: boolean;
  getAuthToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const envSupabaseUrl =
  process.env.REACT_APP_SUPABASE_URL || 'http://localhost:54321';
// Use Cloud URL when env points to local Supabase (127.0.0.1/localhost:54321)
// so Google OAuth works without running local Supabase
const supabaseUrl =
  envSupabaseUrl.includes('127.0.0.1') || envSupabaseUrl.includes('localhost:54321')
    ? (process.env.REACT_APP_SUPABASE_CLOUD_URL || 'https://vkqiqaxzkdcajylrmkhk.supabase.co')
    : envSupabaseUrl;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (process.env.NODE_ENV === 'development' && (!supabaseAnonKey || supabaseAnonKey.length < 50)) {
  console.warn(
    '[Auth] REACT_APP_SUPABASE_ANON_KEY is missing or too short. Restart the dev server after updating frontend/.env'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    flowType: 'pkce',
    detectSessionInUrl: true,
  },
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signInWithUsernameOrEmail = async (
    usernameOrEmail: string,
    password: string
  ) => {
    if (usernameOrEmail.includes('@')) {
      await signIn(usernameOrEmail, password);
      return;
    }
    const baseUrl = getApiUrl().replace(/\/api\/?$/, '');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    let response: Response;
    try {
      response = await fetch(`${baseUrl}/api/auth/sign-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usernameOrEmail, password }),
        signal: controller.signal,
      });
    } catch (e) {
      clearTimeout(timeoutId);
      if (e instanceof Error && e.name === 'AbortError') {
        throw new Error('Request timed out. Make sure the API is running on port 3001.');
      }
      throw e;
    }
    clearTimeout(timeoutId);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Sign in failed');
    }
    const { session } = await response.json();
    if (session) {
      const { error } = await supabase.auth.setSession(session);
      if (error) throw error;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    username?: string
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: username ? { data: { username } } : undefined,
    });
    if (error) throw error;
  };

  const signUpWithUsername = async (username: string, password: string) => {
    const baseUrl = getApiUrl().replace(/\/api\/?$/, '');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    let response: Response;
    try {
      response = await fetch(`${baseUrl}/api/auth/sign-up`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        signal: controller.signal,
      });
    } catch (e) {
      clearTimeout(timeoutId);
      if (e instanceof Error && e.name === 'AbortError') {
        throw new Error('Request timed out. Make sure the API is running on port 3001.');
      }
      throw e;
    }
    clearTimeout(timeoutId);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Sign up failed');
    }
  };

  const signInWithGoogle = async () => {
    const frontendUrl =
      process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${frontendUrl}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    // Use environment variable if available, otherwise use current origin
    const frontendUrl =
      process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${frontendUrl}/reset-password`,
    });
    if (error) throw error;
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({
      password: password,
    });
    if (error) throw error;
  };

  const getAuthToken = async (): Promise<string | null> => {
    try {
      // Get current session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Error getting session:', sessionError);
        return null;
      }

      // If no session, return null
      if (!session) {
        return null;
      }

      // Check if session is expired (with 5 minute buffer)
      const expiresAt = session.expires_at;
      if (expiresAt) {
        const expiresIn = expiresAt - Math.floor(Date.now() / 1000);
        // If token expires in less than 5 minutes, refresh it
        if (expiresIn < 300) {
          const {
            data: { session: refreshedSession },
            error: refreshError,
          } = await supabase.auth.refreshSession();

          if (refreshError) {
            console.error('Error refreshing session:', refreshError);
            return null;
          }

          return refreshedSession?.access_token || null;
        }
      }

      return session.access_token || null;
    } catch (error) {
      console.error('Error in getAuthToken:', error);
      return null;
    }
  };

  const value = {
    user,
    supabase,
    signIn,
    signInWithUsernameOrEmail,
    signUp,
    signUpWithUsername,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    loading,
    getAuthToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
